import React, { useEffect, useRef, useState } from 'react';
import LanguageSwitcher from '../../components/LanguageSwitcher';
import { LucidLogo } from '../../components/icons/LucidLogo';
import type { LandingContent } from '../content';
import { LandingCtaButton } from '../components/LandingCtaButton';
import { SectionContainer } from '../components/SectionContainer';
import type { LandingSectionProps } from '../types';
import { cn } from '../utils';

type HeroSectionProps = LandingSectionProps & {
  content: LandingContent['hero'];
};

function HeroFeatureIcon({
  type,
}: {
  type: 'memory' | 'scene' | 'relationships' | 'world' | 'chapters' | 'access';
}) {
  if (type === 'memory') {
    return (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M8 4.75h8A3.25 3.25 0 0 1 19.25 8v8A3.25 3.25 0 0 1 16 19.25H8A3.25 3.25 0 0 1 4.75 16V8A3.25 3.25 0 0 1 8 4.75Z" stroke="currentColor" strokeWidth="1.6" />
        <path d="M8.5 9h7M8.5 12h7M8.5 15h4.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    );
  }

  if (type === 'scene') {
    return (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M4.75 7.5h14.5M4.75 12h14.5M4.75 16.5h9.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        <path d="M5.5 4.75h13a1.75 1.75 0 0 1 1.75 1.75v11a1.75 1.75 0 0 1-1.75 1.75h-13a1.75 1.75 0 0 1-1.75-1.75v-11A1.75 1.75 0 0 1 5.5 4.75Z" stroke="currentColor" strokeWidth="1.6" />
      </svg>
    );
  }

  if (type === 'relationships') {
    return (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M8.75 10.75a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M15.75 9.75a2 2 0 1 0 0-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M4.75 17.25a4 4 0 0 1 8 0" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M14.25 16.25a3.25 3.25 0 0 1 4.75-.75" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }

  if (type === 'world') {
    return (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M12 20a8 8 0 1 0 0-16 8 8 0 0 0 0 16Z" stroke="currentColor" strokeWidth="1.6" />
        <path d="M4.5 12h15M12 4.5c2.4 2.4 2.4 12.6 0 15M12 4.5c-2.4 2.4-2.4 12.6 0 15" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      </svg>
    );
  }

  if (type === 'chapters') {
    return (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M7 4.75h10A2.25 2.25 0 0 1 19.25 7v10A2.25 2.25 0 0 1 17 19.25H7A2.25 2.25 0 0 1 4.75 17V7A2.25 2.25 0 0 1 7 4.75Z" stroke="currentColor" strokeWidth="1.6" />
        <path d="M8.5 9h7M8.5 12h7M8.5 15h5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    );
  }

  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 4.75a3.75 3.75 0 0 0-3.75 3.75v1h7.5v-1A3.75 3.75 0 0 0 12 4.75Z" stroke="currentColor" strokeWidth="1.6" />
      <path d="M7.75 9.5h8.5a1.5 1.5 0 0 1 1.5 1.5V17a2.25 2.25 0 0 1-2.25 2.25h-7A2.25 2.25 0 0 1 6.25 17v-6a1.5 1.5 0 0 1 1.5-1.5Z" stroke="currentColor" strokeWidth="1.6" />
      <path d="M12 13v2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

export function HeroSection({ t, tracking, content }: HeroSectionProps) {
  const initialIsMobile =
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(max-width: 640px)').matches;
  const initialHeroSlide = 0;

  const [heroIsMobile, setHeroIsMobile] = useState(initialIsMobile);
  const [heroSlide, setHeroSlide] = useState(initialHeroSlide);
  const [heroRenderSlide, setHeroRenderSlide] = useState(initialHeroSlide);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [autoplayPaused, setAutoplayPaused] = useState(false);
  const [heroReveal, setHeroReveal] = useState({
    badge: false,
    title: false,
    description: false,
    bullets: false,
    cta: false,
    preview: false,
  });
  const [reducedMotion, setReducedMotion] = useState(false);

  const renderSlideRef = useRef(initialHeroSlide);
  const autoplayResumeTimeoutRef = useRef<number | null>(null);
  const showcaseRef = useRef<HTMLDivElement | null>(null);
  const tiltRafRef = useRef<number | null>(null);
  const tiltProgressRef = useRef(0);
  const heroSlides = heroIsMobile ? content.mobileSlides : content.slides;

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return;
    }

    const mobileQuery = window.matchMedia('(max-width: 640px)');
    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    const applyMobile = () => setHeroIsMobile(mobileQuery.matches);
    const applyMotion = () => setReducedMotion(motionQuery.matches);

    applyMobile();
    applyMotion();

    if (typeof mobileQuery.addEventListener === 'function') {
      mobileQuery.addEventListener('change', applyMobile);
      motionQuery.addEventListener('change', applyMotion);
      return () => {
        mobileQuery.removeEventListener('change', applyMobile);
        motionQuery.removeEventListener('change', applyMotion);
      };
    }

    mobileQuery.addListener(applyMobile);
    motionQuery.addListener(applyMotion);
    return () => {
      mobileQuery.removeListener(applyMobile);
      motionQuery.removeListener(applyMotion);
    };
  }, []);

  useEffect(() => {
    if (reducedMotion) {
      setHeroReveal({
        badge: true,
        title: true,
        description: true,
        bullets: true,
        cta: true,
        preview: true,
      });
      return;
    }

    setHeroReveal({
      badge: false,
      title: false,
      description: false,
      bullets: false,
      cta: false,
      preview: false,
    });

    const timers = [
      window.setTimeout(() => setHeroReveal((prev) => ({ ...prev, badge: true })), 80),
      window.setTimeout(() => setHeroReveal((prev) => ({ ...prev, title: true })), 180),
      window.setTimeout(() => setHeroReveal((prev) => ({ ...prev, description: true })), 360),
      window.setTimeout(() => setHeroReveal((prev) => ({ ...prev, bullets: true })), 520),
      window.setTimeout(() => setHeroReveal((prev) => ({ ...prev, cta: true })), 700),
      window.setTimeout(() => setHeroReveal((prev) => ({ ...prev, preview: true })), 900),
    ];

    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [reducedMotion]);

  useEffect(() => {
    if (autoplayPaused || reducedMotion) {
      return;
    }

    const interval = window.setInterval(() => {
      setHeroSlide((prev) => (prev + 1) % heroSlides.length);
    }, heroIsMobile ? 4000 : 5000);

    return () => window.clearInterval(interval);
  }, [autoplayPaused, heroIsMobile, heroSlides.length, reducedMotion]);

  useEffect(() => {
    if (heroSlides.length === 0) {
      return;
    }

    const fallbackSlide = Math.min(1, heroSlides.length - 1);

    if (heroSlide >= heroSlides.length) {
      setHeroSlide(fallbackSlide);
    }

    if (heroRenderSlide >= heroSlides.length) {
      renderSlideRef.current = fallbackSlide;
      setHeroRenderSlide(fallbackSlide);
    }
  }, [heroRenderSlide, heroSlide, heroSlides.length]);

  useEffect(() => {
    if (heroSlide === renderSlideRef.current) {
      return;
    }

    setIsTransitioning(true);
    const nextSlide = heroSlide;

    const swapTimer = window.setTimeout(() => {
      renderSlideRef.current = nextSlide;
      setHeroRenderSlide(nextSlide);
    }, 180);

    const finishTimer = window.setTimeout(() => {
      setIsTransitioning(false);
    }, 300);

    return () => {
      window.clearTimeout(swapTimer);
      window.clearTimeout(finishTimer);
    };
  }, [heroSlide]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (reducedMotion) {
      tiltProgressRef.current = 1;
      const node = showcaseRef.current;
      if (node) {
        node.style.transform = 'perspective(1600px) rotateX(0deg) rotateZ(0deg) translateY(0px) scale(1)';
      }
      return;
    }

    const computeProgress = () => {
      const node = showcaseRef.current;
      if (!node) {
        return;
      }

      const rect = node.getBoundingClientRect();
      const viewportHeight = window.innerHeight || 1;
      const start = viewportHeight * 0.58;
      const end = viewportHeight * 0.12;
      const raw = (start - rect.top) / (start - end);
      const clamped = Math.max(0, Math.min(1, raw));

      if (Math.abs(clamped - tiltProgressRef.current) < 0.008) {
        return;
      }

      tiltProgressRef.current = clamped;
      const tiltAngle = (heroIsMobile ? 46 : 62) * (1 - clamped);
      const tiltLift = (heroIsMobile ? 84 : 118) * (1 - clamped);
      const tiltScale = 0.6 + clamped * 0.4;

      node.style.transform = `translateZ(0) perspective(1600px) rotateX(${tiltAngle}deg) rotateZ(0deg) translateY(${tiltLift}px) scale(${tiltScale})`;
    };

    const requestCompute = () => {
      if (tiltRafRef.current !== null) {
        return;
      }

      tiltRafRef.current = window.requestAnimationFrame(() => {
        tiltRafRef.current = null;
        computeProgress();
      });
    };

    const initTilt = () => {
      const node = showcaseRef.current;
      if (!node) {
        return;
      }

      node.style.transformOrigin = '50% 8%';
      node.style.willChange = 'transform';
      node.style.backfaceVisibility = 'hidden';
      node.style.webkitBackfaceVisibility = 'hidden';
    };

    initTilt();
    computeProgress();
    window.addEventListener('scroll', requestCompute, { passive: true });
    window.addEventListener('resize', requestCompute);

    return () => {
      window.removeEventListener('scroll', requestCompute);
      window.removeEventListener('resize', requestCompute);

      if (tiltRafRef.current !== null) {
        window.cancelAnimationFrame(tiltRafRef.current);
        tiltRafRef.current = null;
      }
    };
  }, [heroIsMobile, reducedMotion]);

  useEffect(() => {
    return () => {
      if (autoplayResumeTimeoutRef.current) {
        window.clearTimeout(autoplayResumeTimeoutRef.current);
      }
    };
  }, []);

  const pauseAutoplay = (resumeAfterMs = 8000) => {
    setAutoplayPaused(true);

    if (autoplayResumeTimeoutRef.current) {
      window.clearTimeout(autoplayResumeTimeoutRef.current);
    }

    autoplayResumeTimeoutRef.current = window.setTimeout(() => {
      setAutoplayPaused(false);
    }, resumeAfterMs);
  };

  const activeSlide = heroSlides[Math.max(0, Math.min(heroRenderSlide, heroSlides.length - 1))];
  const descriptionLines = content.description.split(/\n{2,}/).filter(Boolean);
  const renderDescriptionLine = (line: string) => {
    const italicPattern = /\*(.*?)\*/g;
    const nodes: React.ReactNode[] = [];
    let cursor = 0;
    let match: RegExpExecArray | null;

    while ((match = italicPattern.exec(line)) !== null) {
      if (match.index > cursor) {
        nodes.push(line.slice(cursor, match.index));
      }
      nodes.push(
        <em key={`hero-em-${match.index}`} className="font-medium italic">
          {match[1] ?? ''}
        </em>
      );
      cursor = match.index + match[0].length;
    }

    if (cursor < line.length) {
      nodes.push(line.slice(cursor));
    }

    return nodes.length > 0 ? nodes : line;
  };

  return (
    <section className="relative overflow-hidden bg-white text-white">
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        aria-hidden="true"
        style={{ backgroundImage: 'url(/landing/hero-bg-main.png)' }}
      />
      <div
        className="absolute inset-0"
        aria-hidden="true"
        style={{
          backdropFilter: 'blur(80px)',
          background: 'rgba(255,255,255,0.01)',
        }}
      />
      <div
        className="absolute inset-x-0 bottom-0 h-[664px] bg-bottom bg-repeat-x bg-[length:100%_100%] sm:h-[620px] lg:h-[581px]"
        aria-hidden="true"
        style={{ backgroundImage: 'url(/landing/hero-bg-fade.png)' }}
      />
      <div className="absolute inset-x-0 bottom-0 h-[220px] bg-gradient-to-b from-transparent via-[rgba(255,255,255,0.62)] to-white" aria-hidden="true" />

      <SectionContainer className="relative z-10 min-h-screen pb-14 pt-3 sm:pt-5 lg:pb-20">
        <div className="flex items-start justify-between gap-4">
          <div className="pt-1 sm:pt-0">
            <LucidLogo />
          </div>
          <LanguageSwitcher />
        </div>

        <div className="mx-auto flex max-w-[926px] flex-col items-center pt-16 text-center sm:pt-20 lg:pt-[72px]">
          <div
            className={cn(
              'transition-all duration-700 ease-out',
              heroReveal.badge ? 'translate-y-0 opacity-100' : 'translate-y-5 opacity-0'
            )}
          >
            <LandingCtaButton
              label={content.badge}
              placement="hero_badge"
              tracking={tracking}
              variant="ghost"
              size="sm"
              className="border-white/22 bg-white/10 px-3 py-1 text-[12px] leading-[16px] text-white/92 backdrop-blur-md sm:px-4"
            />
          </div>

          <h1
            className={cn(
              'mt-5 max-w-[926px] whitespace-pre-line font-pangaia text-[48px] font-bold leading-[56px] tracking-[-0.04em] text-white transition-all duration-700 ease-out sm:text-[62px] sm:leading-[0.98] lg:text-[76px]',
              heroReveal.title ? 'translate-y-0 opacity-100' : 'translate-y-5 opacity-0'
            )}
          >
            {content.title}
          </h1>

          <div className="mt-4 max-w-[691px]">
            {descriptionLines.map((line, index) => (
              <p
                key={`hero-description-${index}`}
                className={cn(
                  'font-pangram text-[16px] leading-[24px] text-white/84 transition-all duration-700 ease-out sm:text-[14px] sm:leading-[22px]',
                  index === 0 ? 'mt-0' : 'mt-2.5',
                  heroReveal.description ? 'translate-y-0 opacity-100' : 'translate-y-5 opacity-0'
                )}
                style={!heroReveal.description ? undefined : { transitionDelay: `${index * 180}ms` }}
              >
                {renderDescriptionLine(line)}
              </p>
            ))}
          </div>

          <div
            className={cn(
              'mt-8 grid w-full max-w-[360px] grid-cols-1 gap-x-3 gap-y-2.5 transition-all duration-700 ease-out sm:max-w-[900px] sm:grid-cols-3 sm:gap-x-10 sm:gap-y-3',
              heroReveal.bullets ? 'translate-y-0 opacity-100' : 'translate-y-5 opacity-0'
            )}
          >
            {(() => {
              const iconTypes: Array<'memory' | 'scene' | 'relationships' | 'world' | 'chapters' | 'access'> = [
                'memory',
                'scene',
                'relationships',
                'world',
                'chapters',
                'access',
              ];
              return content.bullets.map((bullet, index) => (
                <div
                  key={bullet}
                  className="flex min-h-[24px] items-center gap-3 text-left text-white/94 sm:justify-start"
                >
                  <span className="relative flex h-6 w-6 shrink-0 items-center justify-center text-white/95">
                    <HeroFeatureIcon type={iconTypes[index] ?? 'memory'} />
                  </span>
                  <span className="font-pangram text-[16px] font-semibold leading-[24px] text-white/92 sm:text-[13px] sm:leading-[20px]">
                    {bullet}
                  </span>
                </div>
              ));
            })()}
          </div>

          <div
            className={cn(
              'mt-7 transition-all duration-700 ease-out sm:mt-8',
              heroReveal.cta ? 'translate-y-0 opacity-100' : 'translate-y-5 opacity-0'
            )}
          >
            <LandingCtaButton
              label={t.getStarted || 'Get started'}
              placement="hero_primary"
              tracking={tracking}
              size="md"
              className="border border-white/12 px-6 py-3 text-[16px] shadow-[0_18px_55px_rgba(7,10,18,0.22)]"
            />
          </div>
        </div>

        <div
          className={cn(
            'relative mt-12 [perspective:1900px] transition-all duration-700 ease-out sm:mt-14',
            heroReveal.preview ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
          )}
        >
          <div
            ref={showcaseRef}
            className="[transform-style:preserve-3d]"
          >
            <div
              className="mx-auto max-w-[1120px]"
              onMouseEnter={() => pauseAutoplay(15000)}
              onFocus={() => pauseAutoplay(15000)}
              onClick={() => {
                if (!heroIsMobile || heroSlides.length <= 1) {
                  return;
                }
                setHeroSlide((prev) => (prev + 1) % heroSlides.length);
                pauseAutoplay();
              }}
            >
              <div className="relative overflow-hidden rounded-[8px] p-[2px] backdrop-blur-sm">
                <div className="pointer-events-none absolute inset-0 rounded-[8px]" aria-hidden="true" />
                <picture
                  className={cn(
                    'block overflow-hidden rounded-[8px] transition-all duration-500 ease-out',
                    isTransitioning ? 'translate-y-2 opacity-0' : 'translate-y-0 opacity-100'
                  )}
                >
                  <source media="(max-width: 744px)" srcSet={activeSlide.imageMobile} />
                  <img
                    src={activeSlide.imageDesktop}
                    alt={activeSlide.alt}
                    className={cn(
                      'h-auto w-full rounded-[8px]',
                      heroIsMobile && heroSlides.length > 1 && 'cursor-pointer'
                    )}
                    loading="lazy"
                  />
                </picture>
              </div>
            </div>

            <div className="mx-auto mt-3 flex max-w-[1120px] flex-col gap-2 sm:flex-row sm:flex-nowrap sm:justify-center sm:gap-[12px]">
              {heroSlides.map((slide) => {
                const isActive = slide.id === heroSlide;
                return (
                  <button
                    key={slide.id}
                    type="button"
                    onClick={() => {
                      setHeroSlide(slide.id);
                      pauseAutoplay();
                    }}
                    className={cn(
                      'h-[64px] w-full rounded-[8px] border px-3 py-0 text-left transition-[background-color,border-color,color,box-shadow,transform] duration-300 sm:h-[56px] sm:w-[357px] sm:px-4',
                      isActive
                        ? 'border-transparent bg-[rgba(97,149,255,0.16)] shadow-[0_10px_24px_rgba(97,149,255,0.14)]'
                        : 'border-[#ECEEF4] bg-white hover:border-[#DDE2EC] hover:bg-[#FCFCFF]'
                    )}
                    aria-pressed={isActive}
                  >
                    <span className="flex h-full items-center gap-3 sm:gap-4">
                      <span
                        className={cn(
                          'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-[6px] border font-pangaia text-[24px] font-bold leading-[32px]',
                          isActive
                            ? 'border-[#6195FF] bg-[#6195FF] text-white'
                            : 'border-[#ECEEF4] bg-[#F2F2F7] text-[#8E8E93]'
                        )}
                      >
                        {slide.number}
                      </span>
                      <span
                        className={cn(
                          'line-clamp-2 max-w-[240px] font-pangram text-[16px] font-semibold leading-[20px] tracking-[-0.02em] sm:text-[16px] sm:leading-[24px]',
                          isActive ? 'text-[#6195FF]' : 'text-[#1C1C1E]'
                        )}
                      >
                        {slide.label}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </SectionContainer>
    </section>
  );
}
